// Video validation utilities for exercise feedback uploads

export const MAX_VIDEO_DURATION_SECONDS = 15;
export const MAX_FILE_SIZE_MB = 50;
export const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];

export interface VideoValidationResult {
  valid: boolean;
  error?: string;
  duration?: number;
}

/**
 * Validates a video file for upload
 * Checks file type, size, and duration
 */
export async function validateVideoFile(file: File): Promise<VideoValidationResult> {
  // Check file type
  if (!ALLOWED_VIDEO_TYPES.includes(file.type)) {
    return {
      valid: false,
      error: 'Unsupported video format. Please use MP4, MOV, or WebM.',
    };
  }

  // Check file size
  if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
    return {
      valid: false,
      error: `File too large. Maximum size is ${MAX_FILE_SIZE_MB}MB.`,
    };
  }

  // Check duration
  try {
    const duration = await getVideoDuration(file);
    if (duration > MAX_VIDEO_DURATION_SECONDS) {
      return {
        valid: false,
        error: `Video too long (${Math.round(duration)}s). Maximum duration is ${MAX_VIDEO_DURATION_SECONDS} seconds.`,
        duration,
      };
    }

    return { valid: true, duration };
  } catch {
    // If we can't determine duration, allow it and let server-side validation handle it
    return { valid: true };
  }
}

/**
 * Gets the duration of a video file in seconds
 */
export function getVideoDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';

    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      resolve(video.duration);
    };

    video.onerror = () => {
      URL.revokeObjectURL(video.src);
      reject(new Error('Failed to load video metadata'));
    };

    video.src = URL.createObjectURL(file);
  });
}

/**
 * Formats file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Formats duration for display
 */
export function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}
